import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import type { ConversationRating, RatingStats, RatingValue } from '../types';
import { Star, ThumbsUp, Meh, ThumbsDown, Calendar } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export const Ratings: React.FC = () => {
  const [ratings, setRatings] = useState<ConversationRating[]>([]);
  const [stats, setStats] = useState<RatingStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<RatingValue | 'all'>('all');
  useAuth(); // Keep auth context active

  useEffect(() => {
    loadRatings();
    loadStats();
  }, [filter]);

  const loadRatings = async () => {
    try {
      const params: any = {};
      if (filter !== 'all') {
        params.rating = filter;
      }
      const data = await api.getRatings(params);
      setRatings(data);
    } catch (error) {
      console.error('Failed to load ratings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await api.getRatingStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to load rating stats:', error);
    }
  };

  const getRatingColor = (rating: RatingValue) => {
    switch (rating) {
      case 'BOM':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      case 'MEDIO':
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'RUIM':
        return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
    }
  };

  const getRatingIcon = (rating: RatingValue) => {
    switch (rating) {
      case 'BOM':
        return <ThumbsUp className="w-4 h-4" />;
      case 'MEDIO':
        return <Meh className="w-4 h-4" />;
      case 'RUIM':
        return <ThumbsDown className="w-4 h-4" />;
    }
  };

  const getRatingLabel = (rating: RatingValue) => {
    switch (rating) {
      case 'BOM':
        return 'Bom';
      case 'MEDIO':
        return 'Médio';
      case 'RUIM':
        return 'Ruim';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Avaliações</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Visualizar avaliações dos atendimentos</p>
        </div>
        <button
          onClick={() => {
            loadRatings();
            loadStats();
          }}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-lg transition"
        >
          Atualizar
        </button>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{stats.total}</p>
              </div>
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-full">
                <Star className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Bom</p>
                <p className="text-3xl font-bold text-green-600 dark:text-green-400 mt-2">{stats.bom}</p>
                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">{stats.bomPercentage}%</p>
              </div>
              <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-full">
                <ThumbsUp className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Médio</p>
                <p className="text-3xl font-bold text-yellow-600 dark:text-yellow-400 mt-2">{stats.medio}</p>
                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">{stats.medioPercentage}%</p>
              </div>
              <div className="p-3 bg-yellow-100 dark:bg-yellow-900/30 rounded-full">
                <Meh className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Ruim</p>
                <p className="text-3xl font-bold text-red-600 dark:text-red-400 mt-2">{stats.ruim}</p>
                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">{stats.ruimPercentage}%</p>
              </div>
              <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-full">
                <ThumbsDown className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <div className="flex items-center space-x-4">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Filtrar por avaliação:
          </label>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as any)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
          >
            <option value="all">Todas</option>
            <option value="BOM">Bom</option>
            <option value="MEDIO">Médio</option>
            <option value="RUIM">Ruim</option>
          </select>
        </div>
      </div>

      {/* Ratings List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        {ratings.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            <Star className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Nenhuma avaliação encontrada</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {ratings.map((rating) => (
              <div
                key={rating.id}
                className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="font-medium text-gray-900 dark:text-white">
                        {rating.conversation.customerName || rating.conversation.customerPhone}
                      </h3>
                      <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded text-xs font-mono">
                        #{rating.conversation.protocolId}
                      </span>
                      <span
                        className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-sm font-medium ${getRatingColor(
                          rating.rating
                        )}`}
                      >
                        {getRatingIcon(rating.rating)}
                        <span>{getRatingLabel(rating.rating)}</span>
                      </span>
                    </div>

                    <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400 mb-2">
                      <span>{rating.conversation.customerPhone}</span>
                      {rating.conversation.department && (
                        <span>• {rating.conversation.department.name}</span>
                      )}
                      {rating.conversation.assignedAgent && (
                        <span>• Atendente: {rating.conversation.assignedAgent.name}</span>
                      )}
                    </div>

                    {rating.comment && (
                      <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                        <p className="text-sm text-gray-700 dark:text-gray-300 italic">
                          "{rating.comment}"
                        </p>
                      </div>
                    )}

                    <div className="flex items-center space-x-2 mt-3 text-xs text-gray-500 dark:text-gray-400">
                      <Calendar className="w-3 h-3" />
                      <span>
                        {formatDistanceToNow(new Date(rating.createdAt), {
                          addSuffix: true,
                          locale: ptBR,
                        })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
